import { Box, InputAdornment } from '@material-ui/core';
import Popper from '@material-ui/core/Popper';
import { makeStyles } from '@material-ui/core/styles';
import filterIcon from 'assets/icon/filter.svg';
import { ReactComponent as SearchIcon } from 'assets/icon/searchIcon.svg';
import PrintButton from 'components/Buttons/PrintButton/print-button';
import CheckboxWithLabel from 'components/CheckboxWithLabel';
import DatePicker from 'components/DatePicker';
import DateTimePicker from 'components/DateTimePicker';
import { TextFieldStyle } from 'components/TextInput';
import { Field, Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import { FilterType } from 'pages/NursingForm/constants';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import { useParams } from 'react-router';
import { useClickAway, useDebounce } from 'react-use';
import theme from 'style/theme';
import styled from 'styled-components';
import { AdaptSelect } from '../../AdaptField';
import { Button, ConfirmBox, SwitchBtn } from '../index';

const FilterStatus = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.radicalRed};
  top: -4px;
  right: -4px;
  position: absolute;
  z-index: 1;
`;

export const SubmitBtn = styled.button`
  && {
    background-color: ${(props) => props.theme.colors.malibu};
    border-radius: 8px;
    border: none;
    min-width: 120px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-family: Poppins;
    font-style: normal;
    font-weight: 600;
    font-size: 12px;
    line-height: 18px;
    color: ${(props) => props.theme.colors.white};
    height: 40px;
    width: 100%;
    display: flex;
    padding: 8px;

    :disabled {
      color: #ffffff;
      background-color: #5cabdc;
      border-color: #212121;
    }

    :hover,
    :active,
    :focus {
      background-color: ${(props) => props.theme.colors.malibu} !important;
    }
    &:disabled {
      opacity: 1;
      background-color: #757677 !important;
    }
    &:disabled:hover {
      background-color: #757677 !important;
      cursor: default !important;
    }
  }
`;

export const CancelBtn = styled.button`
  && {
    background-color: ${(props) => props.theme.colors.radicalRed};
    border-radius: 8px;
    border: none;
    min-width: 120px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-family: Poppins;
    font-style: normal;
    font-weight: 600;
    font-size: 12px;
    line-height: 18px;
    color: ${(props) => props.theme.colors.white};
    height: 40px;
    width: 100%;
    align-items: center;
    display: flex;
    padding: 8px;

    :disabled {
      color: #ffffff;
      background-color: #5cabdc;
      border-color: #212121;
    }

    :hover,
    :active,
    :focus {
      background-color: ${(props) => props.theme.colors.radicalRed} !important;
    }
    &:disabled {
      opacity: 1;
      background-color: #757677 !important;
    }
    &:disabled:hover {
      background-color: #757677 !important;
      cursor: default !important;
    }
  }
`;

export const useSearchbarStyles = makeStyles(() => ({
  root: {
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    textAlign: 'left',
    paddingBottom: 0,
    marginBottom: 8,
    color: theme.colors.eastBay,
    fontWeight: 700,
    justifyContent: 'flex-start',
    '&:hover,&:focus': {
      color: theme.colors.eastBay,
    },
    '& span': {
      width: '100%',
    },
    '& h4': {
      textAlign: 'left',
      fontSize: 18,
      margin: 0,
      padding: 0,
    },
  },
  popper: {
    border: `1px solid ${theme.colors.shadowBorderGrey}`,
    boxShadow: `0 3px 12px ${theme.colors.shadowBorderGrey}`,
    maxWidth: 300,
    width: '100%',
    zIndex: 100,
    fontSize: 14,
    color: theme.colors.eastBay,
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    fontFamily: 'Poppins',
    marginTop: '12px !important',
  },
  inputBase: {
    padding: 10,
    width: '100%',
    // borderBottom: '1px solid #dfe2e5',
    '& input': {
      borderRadius: 8,
      backgroundColor: theme.colors.white,
      padding: '8px 25px',
      border: `1px solid ${theme.colors.ghost}`,
      fontSize: 14,
      '&:focus': {
        boxShadow: `${theme.colors.mainlyblue} 0 0 0 0.2rem`,
        borderColor: theme.colors.malibu06,
      },
    },
  },
  paper: {
    margin: 0,
    color: theme.colors.comet,
    fontSize: 14,
  },
  option: {
    minHeight: 'auto',
    alignItems: 'flex-start',
    padding: 8,
    '&[aria-selected="true"]': {
      backgroundColor: 'transparent',
      zIndex: 2,
    },
  },
  popperDisablePortal: {
    position: 'relative',
    zIndex: 1,
  },
  iconSelected: {
    width: 17,
    height: 17,
    marginRight: 5,
    marginLeft: -2,
  },
  color: {
    width: 14,
    height: 14,
    flexShrink: 0,
    borderRadius: 3,
    marginRight: 8,
    marginTop: 2,
  },
  text: {
    flexGrow: 1,
  },
  close: {
    opacity: 0.6,
    width: 18,
    height: 18,
  },
}));

const SearchBox = styled(TextFieldStyle)``;

export interface FilterExtraDropdownProps {
  label: string;
  options: { label: string; value: string }[];
}

interface ExtendedFilterType extends FilterType {
  extraDropdown?: string;
}

type Props = {
  items?: { label: string; value: string }[] | undefined;
  onClick: () => void;
  onFilterChange?: React.Dispatch<React.SetStateAction<ExtendedFilterType>>;
  isHourFilterable?: boolean;
  addButtonLabel?: string;
  filterExtraDropdown?: FilterExtraDropdownProps;
  disableClickAway?: boolean;
  handlePrint?: () => void;
  disablePrint?: boolean;
  filterDateFrom?: Date;
  filterDateTo?: Date;
  disableAddNew?: boolean;
};

type Values = {
  startDate?: Date;
  endDate?: Date;
  extraDropdown?: string;
} & Record<string, boolean>;

const SearchBar: React.VFC<Props> = ({
  items,
  onClick,
  onFilterChange,
  isHourFilterable = true,
  addButtonLabel,
  filterExtraDropdown,
  disableClickAway = false,
  handlePrint,
  disablePrint = false,
  filterDateFrom,
  filterDateTo,
  disableAddNew,
}) => {
  const { t } = useTranslation();
  const classes = useSearchbarStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [formValues, setFormValues] = useState<Values>({
    startDate: filterDateFrom,
    endDate: filterDateTo,
  } as Values);
  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const clickAwayRef = useRef(null);
  const params = useParams<{ groupId: string; residentId: string }>();

  const initialValues = {
    ...Object.assign({}, formValues),
    startDate: filterDateFrom ? filterDateFrom : undefined,
    endDate: filterDateTo ? filterDateTo : undefined,
  };

  useDebounce(
    () => {
      setSearchValue(searchInput);
    },
    500,
    [searchInput],
  );

  useEffect(() => {
    setSearchInput('');
    setSearchValue('');
  }, [params.residentId]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (open) {
      handleClose();
    }
  };

  const onCancelClick = (event: React.MouseEvent<HTMLElement>) => {
    setFormValues({});
    handleClick(event);
  };

  const handleClose = () => {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const onSubmit = (values: any) => {
    setFormValues(values);
    if (open) {
      handleClose();
    }
  };

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange({
        from: formValues?.startDate ?? undefined,
        to: formValues?.endDate ?? undefined,
        topic: items
          ?.map((item) => (formValues[item.value] ? item.value : undefined))
          .filter((item) => !!item) as string[],
        search: searchValue ?? '',
        extraDropdown: formValues?.extraDropdown ?? undefined,
      });
    }
  }, [searchValue, formValues, onFilterChange]);

  useClickAway(
    clickAwayRef,
    () => {
      if (!disableClickAway) {
        handleClose();
      }
    },
    ['click'],
  );

  const open = Boolean(anchorEl);
  const id = open ? 'github-label' : undefined;

  const hasFilterValue = !isEmpty(
    Object.keys(formValues).reduce((acc, key) => {
      if (formValues[key]) {
        return {
          ...acc,
          [key]: formValues[key],
        };
      } else {
        return acc;
      }
    }, {}),
  );

  return (
    <Box
      ref={clickAwayRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        '@media(max-width: 1280px)': {
          flexWrap: 'wrap',
        },
      }}
    >
      <SearchBox
        id="search"
        sx={{ m: 0, width: '271px' }}
        onChange={(e) => {
          setSearchInput(e.target.value);
        }}
        size="small"
        value={searchInput}
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{
                mr: 1,
              }}
            >
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <SwitchBtn showSubmit={false} style={{ marginLeft: 0, overflow: 'visible' }}>
        <Button
          id="filter-btn"
          onClick={handleClick}
          style={{
            position: 'relative',
          }}
        >
          <SVG src={filterIcon} />
          {hasFilterValue ? <FilterStatus /> : null}
        </Button>
        <Popper
          id={id}
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          className={classes.popper}
        >
          <Box>
            <Formik onSubmit={onSubmit} initialValues={initialValues}>
              {({ values, handleSubmit }) => (
                <Form
                  onSubmit={handleSubmit}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Box sx={{ p: '16px' }} data-test-id="filters-box">
                    <Box sx={{ p: '6px 0px', pb: 0 }}>
                      <Field
                        component={isHourFilterable ? DateTimePicker : DatePicker}
                        name="startDate"
                        label={t('txt_of')}
                        editable={true}
                        inputFormat={isHourFilterable ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy'}
                        maxDateTime={values.endDate}
                        value={filterDateFrom}
                        filter
                      />
                    </Box>
                    <Box sx={{ pb: '6px' }}>
                      <Field
                        component={isHourFilterable ? DateTimePicker : DatePicker}
                        name="endDate"
                        label={t('txt_to')}
                        editable={true}
                        inputFormat={isHourFilterable ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy'}
                        minDateTime={values.startDate}
                        value={filterDateTo}
                        filter
                      />
                    </Box>
                    {filterExtraDropdown && (
                      <Box sx={{ pb: '6px' }}>
                        <Field
                          component={AdaptSelect}
                          editable={true}
                          name="extraDropdown"
                          label={filterExtraDropdown.label}
                          dropdownList={filterExtraDropdown.options}
                          nullable={true}
                          wrapper={{ pt: '3px' }}
                        />
                      </Box>
                    )}
                    {items &&
                      items.map((item) => (
                        <Box sx={{ pb: '16px' }} key={item.value}>
                          <Field
                            component={CheckboxWithLabel}
                            name={item.value}
                            label={item.label}
                            type="checkbox"
                            editable={true}
                          />
                        </Box>
                      ))}
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      <SubmitBtn id="apply-btn" type="submit">
                        {t('apply_btn')}
                      </SubmitBtn>
                      <CancelBtn id="reset-btn" type="button" onClick={onCancelClick}>
                        {t('reset_btn')}
                      </CancelBtn>
                    </Box>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Popper>
      </SwitchBtn>
      <ConfirmBox showSubmit={true} style={{ marginRight: 0 }}>
        <SubmitBtn id="add_report" onClick={() => onClick && onClick()} disabled={disableAddNew}>
          {addButtonLabel ?? t('add_report')}
        </SubmitBtn>
      </ConfirmBox>
      {!disablePrint && <PrintButton handlePrint={handlePrint} disabled={disablePrint} />}
    </Box>
  );
};

export default SearchBar;
